import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';

import { HeatTraceClearCircuits, HeatTraceLoadCircuits } from '@completion/actions';
import { Circuit, CircuitTag, DistributionBoard, Jbox } from '@completion/models';
import { State } from '@completion/reducers';
import { getCircuits, getCircuitsLoading, getCurrentCircuitTag } from '@completion/selectors';

@Component({
  selector: 'app-circuit-diagram',
  templateUrl: './circuit-diagram.component.html',
  styleUrls: ['./circuit-diagram.component.scss'],
  standalone: false,
})
export class CircuitDiagramComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  focusedPath: string[] = [];
  isMinified = false;

  cpMcpRoute(entity: Record<string, unknown> | null | undefined): (string | number)[] | null {
    if (!entity) {
      return null;
    }

    const cpId = this.getNumericId(entity, 'cpId', 'cpID');
    const mcpId = this.getNumericId(entity, 'mcpId', 'mcpID');
    if (cpId == null || mcpId == null) {
      return null;
    }

    return ['/cp', cpId, 'mcp', mcpId];
  }

  isClickableEntity(entity: Record<string, unknown> | null | undefined): boolean {
    return this.cpMcpRoute(entity) != null;
  }

  tagQueryParams(tagNumber: string | null | undefined): { tag: string } | null {
    const tag = tagNumber?.trim();
    return tag ? { tag } : null;
  }

  tagNumber$ = this.route.paramMap.pipe(
    map((paramMap) => paramMap.get('tagNumber')),
    distinctUntilChanged(),
  );

  circuits$ = this.store.select(getCircuits);
  circuitsLoading$ = this.store.select(getCircuitsLoading);
  currentCircuitTag$ = this.store.select(getCurrentCircuitTag);

  displayLabel(label?: string, previousLabel?: string): string {
    if (!label) {
      return '';
    }

    if (!this.isMinified) {
      return label;
    }

    const trimmedLabel = label.trim();
    if (!trimmedLabel) {
      return '';
    }

    const trimmedPrevious = previousLabel?.trim();
    const normalizedCurrent = trimmedLabel.replace(/[^a-z0-9]/gi, '').toUpperCase();
    const normalizedPrevious = trimmedPrevious?.replace(/[^a-z0-9]/gi, '').toUpperCase();

    if (
      normalizedPrevious &&
      normalizedCurrent.length === normalizedPrevious.length + 1 &&
      normalizedCurrent.endsWith(normalizedPrevious) &&
      /^[A-Za-z]$/.test(trimmedLabel.charAt(0))
    ) {
      const prefix = trimmedLabel.charAt(0).toUpperCase();
      const previousSuffix = trimmedPrevious
        ?.split('-')
        .filter((part) => part.length > 0)
        .pop();
      return previousSuffix ? `${prefix}-${previousSuffix}` : prefix;
    }

    if (trimmedPrevious && trimmedLabel.startsWith(trimmedPrevious) && trimmedLabel.length > trimmedPrevious.length) {
      return trimmedLabel.slice(trimmedPrevious.length).replace(/^[-_/.:\s]+/, '') || trimmedLabel;
    }

    const parts = trimmedLabel.split('-').filter((part) => part.length > 0);
    if (parts.length > 1) {
      return parts[parts.length - 1];
    }

    if (trimmedLabel.length <= 3) {
      return trimmedLabel;
    }

    return trimmedLabel.slice(-3);
  }

  toggleMinified(): void {
    this.isMinified = !this.isMinified;
  }

  onFocusRequested(path: string[]): void {
    this.focusedPath = [...path];
  }

  focusOnDistributionBoard(dbNo: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.focusedPath = [dbNo];
  }

  zoomOut(): void {
    if (this.focusedPath.length === 0) {
      return;
    }

    this.focusedPath = this.focusedPath.slice(0, -1);
  }

  zoomToRoot(): void {
    this.focusedPath = [];
  }

  isBoardVisible(db: DistributionBoard): boolean {
    return this.focusedPath.length === 0 || this.focusedPath[0] === db.no;
  }

  shouldShowDistributionBoardRoot(db: DistributionBoard): boolean {
    if (this.focusedPath.length === 0) {
      return true;
    }

    // Keep the containing DB visible when focused at DB level ([db]).
    if (this.focusedPath.length === 1 && this.focusedPath[0] === db.no) {
      return true;
    }

    return this.isCubicleFocus(db);
  }

  private isCubicleFocus(db: DistributionBoard): boolean {
    if (this.focusedPath.length !== 2 || this.focusedPath[0] !== db.no) {
      return false;
    }

    const focusedCircuitNo = this.focusedPath[1];
    return db.circuits.some((circuit) => circuit.no === focusedCircuitNo);
  }

  pathPrefix(db: DistributionBoard): string[] {
    if (this.focusedPath.length > 0 && this.focusedPath[0] === db.no) {
      return [...this.focusedPath];
    }

    return [db.no];
  }

  focusedRootJbox(db: DistributionBoard): Jbox | undefined {
    if (this.focusedPath.length <= 1 || this.focusedPath[0] !== db.no) {
      return undefined;
    }

    const segments = this.focusedPath.slice(1);
    let circuits = db.circuits;
    let startIndex = 0;

    const focusedCircuit = segments.length > 0 ? circuits.find((circuit) => circuit.no === segments[0]) : undefined;
    if (focusedCircuit) {
      circuits = [focusedCircuit];
      startIndex = 1;
    }

    let focusedJbox: Jbox | undefined;
    for (const jboxNo of segments.slice(startIndex)) {
      focusedJbox = circuits.flatMap((circuit) => circuit.tags).find((tag): tag is Jbox => this.isJbox(tag) && tag.no === jboxNo);

      if (!focusedJbox) {
        return undefined;
      }

      circuits = focusedJbox.circuits ?? [];
    }

    return focusedJbox;
  }

  isFocusedJboxRoot(db: DistributionBoard): boolean {
    return !!this.focusedRootJbox(db);
  }

  visibleCircuits(db: DistributionBoard): Circuit[] {
    if (this.focusedPath.length === 0) {
      return db.circuits;
    }

    if (this.focusedPath[0] !== db.no) {
      return [];
    }

    const segments = this.focusedPath.slice(1);
    let circuits = db.circuits;
    let startIndex = 0;

    const focusedCircuit = segments.length > 0 ? circuits.find((circuit) => circuit.no === segments[0]) : undefined;
    if (focusedCircuit) {
      circuits = [focusedCircuit];
      startIndex = 1;
    }

    for (const jboxNo of segments.slice(startIndex)) {
      const matchingJbox = circuits
        .flatMap((circuit) => circuit.tags)
        .find((tag): tag is Jbox => this.isJbox(tag) && tag.no === jboxNo && !!tag.circuits?.length);

      if (!matchingJbox?.circuits) {
        return [];
      }

      circuits = matchingJbox.circuits;
    }

    return circuits;
  }

  breadcrumb(): string {
    return this.focusedPath
      .map((segment, index, allSegments) => this.displayLabel(segment, index > 0 ? allSegments[index - 1] : undefined))
      .join(' > ');
  }

  private getNumericId(entity: Record<string, unknown>, ...keys: string[]): number | null {
    for (const key of keys) {
      const value = entity[key];
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }
      if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) {
          return parsed;
        }
      }
    }

    return null;
  }

  private isJbox(tag: CircuitTag): tag is Jbox {
    return tag.kind === 'JUNCTION_BOX';
  }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly store: Store<State>,
  ) {}

  ngOnInit(): void {
    this.tagNumber$
      .pipe(
        filter((tagNumber): tagNumber is string => tagNumber !== null),
        takeUntil(this.destroy$),
      )
      .subscribe((tagNumber) => {
        this.focusedPath = [];
        this.store.dispatch(new HeatTraceLoadCircuits(tagNumber));
      });
  }

  ngOnDestroy(): void {
    this.store.dispatch(new HeatTraceClearCircuits());
    this.destroy$.next();
    this.destroy$.complete();
  }
}
